import { createEffect, location, onCleanup, onMount, reactive, untrack } from 'cdui-js';
import { LastNextBtns } from '../../components/LastNextBtns';
import { editProcess, lastProcess, nextProcess, pState, pushVisit, refreshApplyByUserId } from '../../store';
import { showToast, showToastError } from '../../../../components/Toast';
import i18n from '../../../../../../i18n';
import {
  verifyOpenProfileFingerprint,
  type VerifyOpenProfileFingerprintResult,
} from '../../../../../../api/open-account';
import { userState } from '../../../../../../stores/user';
import {
  captureCertificateSerialFromQuery,
  hasSzcaPendingSession,
  hydrateSzcaCertToPState,
  SZCA_SERIAL_QUERY_KEY,
} from './szca-face';
import {
  isSzcaReturnForContext,
  runSzcaAuthFlow,
  runSzcaShortAuthFlow,
  tryCompleteSzcaReturn,
  type SzcaAuthContext,
} from './runSzcaAuthFlow';
import { isSzcaReturnFromQuery, SZCA_RETURN_QUERY_KEY } from './openSzcaAuthViaApp';
import { stepOf } from '../stepMaps';

/** URL：profileFix=id,bank | id | bank（可覆盖 idCardPassed / bankCardPassed） */
export const PROFILE_FIX_QUERY_KEY = 'profileFix';

/** 开户用 riskInfo；修改模式用 editRefusedRisk（回跳 phase=risk） */
const getSzcaContext = (): SzcaAuthContext =>
  pState.editRefusedMode ? 'editRefusedRisk' : 'riskInfo';

/** 仅对显式 false 生效（兼容旧接口未返回该字段） */
const isExplicitFalse = (v: unknown) =>
  v === false || v === 0 || String(v).toLowerCase() === 'false' || String(v) === '0';

const isExplicitFlagFalse = (v: unknown) =>
  v !== undefined && v !== null && v !== '' && isExplicitFalse(v);

type ProfilePassFlags = {
  idCardPassed?: unknown;
  bankCardPassed?: unknown;
};

type ProfileFixPages = {
  idCard: boolean;
  bankCard: boolean;
};

/**
 * 解析 profileFix：id / bank / id,bank（亦兼容 idCard / bankCard）。
 * 无有效值时返回 null（改用 idCardPassed / bankCardPassed）。
 */
const parseProfileFixQuery = (raw: unknown): ProfileFixPages | null => {
  if (raw === undefined || raw === null || String(raw).trim() === '') return null;
  const parts = String(raw)
    .toLowerCase()
    .split(/[,|+\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (!parts.length) return null;

  let idCard = false;
  let bankCard = false;
  for (const p of parts) {
    if (p === 'id' || p === 'idcard' || p === 'lic') idCard = true;
    if (p === 'bank' || p === 'bankcard' || p === 'unionpay') bankCard = true;
  }
  if (!idCard && !bankCard) return null;
  return { idCard, bankCard };
};

/** 由失败标记推页面；两边都 false → 双页 */
const resolvePagesFromFlags = (flags: ProfilePassFlags): ProfileFixPages | null => {
  const idCard = isExplicitFlagFalse(flags.idCardPassed);
  const bankCard = isExplicitFlagFalse(flags.bankCardPassed);
  if (!idCard && !bankCard) return null;
  return { idCard, bankCard };
};

/**
 * 优先 URL profileFix；否则看 idCardPassed / bankCardPassed。
 * 两边都需改时返回双页（id+bank）。
 */
const resolveProfileFixPages = (flags: ProfilePassFlags): ProfileFixPages | null => {
  const fromQuery = parseProfileFixQuery(location.query[PROFILE_FIX_QUERY_KEY]);
  if (fromQuery) return fromQuery;
  return resolvePagesFromFlags(flags);
};

/** 是否仍有需纠错页（用于判断指纹是否已通过） */
const hasFailedProfilePass = (flags: ProfilePassFlags) => !!resolvePagesFromFlags(flags);

/** CA 回跳 URL 上的 idCardPassed / bankCardPassed */
const readProfilePassFlagsFromQuery = (): ProfilePassFlags => ({
  idCardPassed: location.query.idCardPassed,
  bankCardPassed: location.query.bankCardPassed,
});

/**
 * 去掉 CA 失败回跳相关 query，避免返回 RiskInfo 后仍被 URL 判定为「刚回跳」。
 * 保留 step / mode / phase / embed 等其它参数。
 */
const stripProfilePassQueryFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of [
      SZCA_RETURN_QUERY_KEY,
      'idCardPassed',
      'bankCardPassed',
      PROFILE_FIX_QUERY_KEY,
      SZCA_SERIAL_QUERY_KEY,
    ]) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (!changed) return;

    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, '', next);

    const q = location.query as Record<string, string | undefined>;
    delete q[SZCA_RETURN_QUERY_KEY];
    delete q.idCardPassed;
    delete q.bankCardPassed;
    delete q[PROFILE_FIX_QUERY_KEY];
    delete q[SZCA_SERIAL_QUERY_KEY];
  } catch {
    /* ignore */
  }
};

/** 仅去掉 ca=1，退出「刚回跳」态（无 pending 无法收尾时用） */
const stripCaReturnQueryFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(SZCA_RETURN_QUERY_KEY)) return;
    url.searchParams.delete(SZCA_RETURN_QUERY_KEY);
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, '', next);
    delete (location.query as Record<string, string | undefined>)[SZCA_RETURN_QUERY_KEY];
  } catch {
    /* ignore */
  }
};

/**
 * 身份证 / 银行卡纠错跳转。
 * - 仅身份证 → UploadLic
 * - 仅银行卡 → UploadBank
 * - 两边都需改 → UploadLic→UploadBank 区间（身份证无返回，银行卡返回 RiskInfo）
 * 正常开户走 editProcess；editRefused 退回 forms 阶段。
 */
const redirectForFailedProfilePass = (flags: ProfilePassFlags): boolean => {
  const pages = resolveProfileFixPages(flags);
  if (!pages) return false;

  // App/流程中跳页保留 CA 本地缓存，仅在 finishOpenAccount 时清理
  stripProfilePassQueryFromUrl();

  if (pState.editRefusedMode) {
    pushVisit({ step: pState.current, editRefusedPhase: 'forms' }, { completePrev: false });
    pState.editRefusedFormStep = 0;
    return true;
  }

  const isMainland = pState.uploadLicForm.idCardType === 'MAINLAND_CHINA';
  const licStep = stepOf('UploadLic', isMainland);
  const bankStep = isMainland ? stepOf('UploadBank', true) : -1;

  if (pages.idCard && pages.bankCard) {
    if (!isMainland || bankStep < 0) {
      // 非大陆无银联页：只改身份证
      pState.profilePassDualFix = false;
      pState.profilePassAutoRedirected = true;
      editProcess(licStep);
      return true;
    }
    pState.profilePassDualFix = true;
    pState.profilePassAutoRedirected = true;
    editProcess(licStep, bankStep);
    return true;
  }

  if (pages.idCard) {
    pState.profilePassDualFix = false;
    pState.profilePassAutoRedirected = true;
    editProcess(licStep);
    return true;
  }

  if (pages.bankCard && isMainland && bankStep >= 0) {
    pState.profilePassDualFix = false;
    pState.profilePassAutoRedirected = true;
    editProcess(bankStep);
    return true;
  }

  return false;
};

type RiskInfoBoot = {
  fingerprint: VerifyOpenProfileFingerprintResult | null;
};

/** 壳重建时多个 RiskInfo 共用同一次进页请求；离开步骤后再清空 */
let riskInfoMounts = 0;
let riskInfoBootPromise: Promise<RiskInfoBoot> | null = null;

const clearRiskInfoBootIfIdle = () => {
  queueMicrotask(() => {
    if (riskInfoMounts === 0) riskInfoBootPromise = null;
  });
};

/** 等 getByUserId 结束再请求，避免壳先挂上时打出空数据 */
const runRiskInfoBoot = async (): Promise<RiskInfoBoot> => {
  const ctx = getSzcaContext();
  // 已从身份证/银行卡页返回：不再自动踢走，仅点「下一步」时再校验
  const skipAutoRedirect = pState.profilePassAutoRedirected;
  const empty: RiskInfoBoot = { fingerprint: null };

  // CA 整页回跳：再拉一次 getByUserId，确保 KYC 表单已从服务端回填（兜底 init 漏网）
  if (isSzcaReturnForContext(ctx)) {
    try {
      const applyType = userState.applyStatus !== 'FINISHED' ? 'NEW_APPLY' : 'ADD_ACCOUNT';
      await refreshApplyByUserId(applyType);
    } catch (e) {
      showToastError(e);
      return empty;
    }
  }

  const isMainland = pState.uploadLicForm.idCardType === 'MAINLAND_CHINA';

  // 整页回跳后内存清空：先从 localStorage 恢复 certDn/certSn；URL serial 单独落盘
  if (isMainland) {
    hydrateSzcaCertToPState(pState);
    if (isSzcaReturnForContext(ctx)) {
      captureCertificateSerialFromQuery(pState.id);
    }
  }

  // CA 回跳若带失败标记 / profileFix，优先跳转上传页，不再完成 findCertDown
  if (
    !skipAutoRedirect &&
    isMainland &&
    isSzcaReturnForContext(ctx) &&
    redirectForFailedProfilePass(readProfilePassFlagsFromQuery())
  ) {
    return empty;
  }

  let fingerprint: VerifyOpenProfileFingerprintResult | null = null;
  try {
    fingerprint = await verifyOpenProfileFingerprint();
  } catch (e) {
    showToastError(e);
    return empty;
  }

  if (fingerprint && !hasFailedProfilePass(fingerprint)) {
    // 资料已通过：允许下次 CA 失败再次自动跳转
    pState.profilePassAutoRedirected = false;
  }

  if (!skipAutoRedirect && fingerprint && redirectForFailedProfilePass(fingerprint)) {
    return { fingerprint };
  }

  if (!isMainland) return { fingerprint };

  // 回跳收尾：仅当仍有 pending 时走 findCertDown；无 pending 则去掉 ca，避免卡住回跳态
  if (isSzcaReturnForContext(ctx)) {
    if (hasSzcaPendingSession()) {
      try {
        await tryCompleteSzcaReturn({ context: ctx, onSuccess: nextProcess });
      } catch (e) {
        showToastError(e);
      }
    } else {
      stripCaReturnQueryFromUrl();
    }
  }

  return { fingerprint };
};

const ensureRiskInfoBoot = () => {
  if (!riskInfoBootPromise) {
    riskInfoBootPromise = runRiskInfoBoot().catch((error) => {
      riskInfoBootPromise = null;
      throw error;
    });
  }
  return riskInfoBootPromise;
};

export const RiskInfo = () => {
  const state = reactive({
    loading: false,
    fingerprintLoading: false,
    /**
     * CA 回跳硬锁：首帧即 true，避免 fingerprintLoading 初始 false / 壳重建空窗击穿下一步。
     * boot（指纹 + findCertDown）结束后再放开。
     */
    caSettling: isSzcaReturnFromQuery(),
    profileFingerprint: null as VerifyOpenProfileFingerprintResult | null,
  });

  const loadProfileFingerprint = async () => {
    state.fingerprintLoading = true;
    try {
      state.profileFingerprint = await verifyOpenProfileFingerprint();
    } catch (e) {
      showToastError(e);
    } finally {
      state.fingerprintLoading = false;
    }
  };

  /**
   * 进页必调指纹；CA 回跳再 findCertDown。
   * 壳会因 loading / 表单回填重建本组件，只允许最后一次挂载消费同一次请求。
   */
  onMount(() => {
    riskInfoMounts++;
    let alive = true;
    onCleanup(() => {
      alive = false;
      riskInfoMounts--;
      clearRiskInfoBootIfIdle();
    });

    // CA 回跳：在等 getByUserId 期间也保持锁定（portal 另有 pState.loading）
    if (isSzcaReturnFromQuery()) {
      state.caSettling = true;
    }

    createEffect(() => {
      if (pState.loading) return;
      untrack(() => {
        if (!alive) return;
        state.fingerprintLoading = true;
        void ensureRiskInfoBoot()
          .then((boot) => {
            if (!alive) return;
            state.profileFingerprint = boot.fingerprint;
          })
          .catch(() => {
            /* runRiskInfoBoot 已 toast；失败时 promise 已清空，下次挂载可重试 */
          })
          .finally(() => {
            if (!alive) return;
            state.fingerprintLoading = false;
            state.caSettling = false;
          });
      });
    });
  });

  const runMainlandNext = async (ctx: SzcaAuthContext) => {
    hydrateSzcaCertToPState(pState);

    if (isSzcaReturnForContext(ctx)) {
      if (redirectForFailedProfilePass(readProfilePassFlagsFromQuery())) return;
      captureCertificateSerialFromQuery(pState.id);
      // 回跳必须走本次 findCertDown；禁止用缓存旧 certSn 直接 nextProcess
      if (!hasSzcaPendingSession()) {
        stripCaReturnQueryFromUrl();
        showToast(i18n.OpenProgress.Risk.Szca.ToastSessionExpired);
        return;
      }
      const ok = await tryCompleteSzcaReturn({ context: ctx, onSuccess: nextProcess });
      if (ok) return;
      return;
    }

    const fp = state.profileFingerprint;
    if (!fp) {
      showToast(i18n.OpenProgress.Risk.Video.ToastServiceError);
      return;
    }

    if (redirectForFailedProfilePass(fp)) return;

    if (fp.valid) {
      // 指纹有效且本地已有 certSn，可直接下一步（须在指纹校验之后，避免过期 CA 误跳过）
      if (pState.certSn && !hasSzcaPendingSession()) {
        nextProcess();
        return;
      }
      if (!fp.certBizNo) {
        showToast(i18n.OpenProgress.Risk.Szca.ToastCertFetchFailed);
        return;
      }
      const ok = await runSzcaShortAuthFlow({
        certBizNo: fp.certBizNo,
        resId: fp.resId,
        context: ctx,
      });
      if (ok) nextProcess();
      return;
    }

    const ok = await runSzcaAuthFlow({ context: ctx, force: true });
    if (ok) nextProcess();
  };

  const isNextBusy = () => state.loading || state.fingerprintLoading || state.caSettling;

  return (
    <div class="padding-x-L margin-y-XL">
      <div class="margin-b-L flex justify-space-between align-center">
        <div class="font-s-L font-bold color-D">{i18n.OpenProgress.Risk.Disclosure.Title}</div>
      </div>

      <div class="margin-t-L color-D">{i18n.OpenProgress.Risk.Disclosure.Intro}</div>
      <div class="margin-t color-D">{i18n.OpenProgress.Risk.Disclosure.Point1}</div>
      <div class="margin-t-XS color-D">{i18n.OpenProgress.Risk.Disclosure.Point2}</div>
      <div class="margin-t-XS color-D">{i18n.OpenProgress.Risk.Disclosure.Point3}</div>
      <div class="margin-t color-D">{i18n.OpenProgress.Risk.Disclosure.Footer}</div>

      <LastNextBtns
        loading={state.loading || state.fingerprintLoading || state.caSettling}
        nextDisabled={state.caSettling}
        last={() => {
          lastProcess();
        }}
        next={async () => {
          if (isNextBusy()) return;
          state.loading = true;
          try {
            if (pState.uploadLicForm.idCardType === 'MAINLAND_CHINA') {
              // 点下一步前刷新指纹，避免沿用跳转前的失败结果
              if (pState.profilePassAutoRedirected || !state.profileFingerprint) {
                await loadProfileFingerprint();
              }
              await runMainlandNext(getSzcaContext());
              return;
            }
            nextProcess();
          } catch (e) {
            showToastError(e);
          } finally {
            state.loading = false;
          }
        }}
      />
    </div>
  );
};
